// Who sees whom and which stores (ports of AppStore.visibleUsers / descendantTeamIds / visibleRecords,
// GroundTeam/Store/AppStore.swift:140-176, Records+Store.canEdit, Reports+Store.leaderboardUsers and manageableGoals).
//
// The viewer's role is the membership role from the session (`SessionCtx.role`), which the server keeps equal to the
// profile's `users.role` (M1:701-769); iOS reads the profile's role. Both agree once the profile has synced.

import type { GoalPayload, RecordPayload, SettingsPayload, TeamPayload, TerritoryPayload, UserPayload } from '@/contracts/models';
import type { Role } from '@/contracts/wire';
import { hasId, idKey, idSet, sameId } from './util';

/** The signed-in member: their `users` profile id (as the membership returned it) and membership role. */
export interface Viewer {
  profileId: string | null | undefined;
  role: Role;
}

/** The viewer's `users` item (`AppStore.currentUser`), if it has synced. */
export function currentUser(users: readonly UserPayload[], viewer: Viewer): UserPayload | undefined {
  return viewer.profileId ? users.find((u) => sameId(u.id, viewer.profileId)) : undefined;
}

/**
 * `AppStore.descendantTeamIds(of:)`: every team below `teamId` via `parentTeamId` (children first, then their
 * children). Guards against cycles, which Swift would recurse on forever.
 */
export function descendantTeamIds(teams: readonly TeamPayload[], teamId: string | null | undefined): string[] {
  if (!teamId) return [];
  const out: string[] = [];
  const seen = new Set<string>([idKey(teamId)]);
  let frontier = [teamId];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const parent of frontier) {
      for (const t of teams) {
        if (!t.parentTeamId || !sameId(t.parentTeamId, parent)) continue;
        const k = idKey(t.id);
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(t.id);
        next.push(t.id);
      }
    }
    frontier = next;
  }
  return out;
}

/**
 * `AppStore.visibleUsers`: admins see everyone; managers see themselves, people whose `managerId` is them, and
 * everyone in the teams they manage (`Team.managerId`), their own team, and every team below those; reps see
 * themselves. Before the viewer's profile has synced, everyone (as iOS does without a current user).
 */
export function visibleUsers(users: readonly UserPayload[], teams: readonly TeamPayload[], viewer: Viewer): UserPayload[] {
  const me = currentUser(users, viewer);
  if (!me) return [...users];
  switch (viewer.role) {
    case 'admin':
      return [...users];
    case 'manager': {
      const managed = teams.filter((t) => sameId(t.managerId, me.id)).map((t) => t.id);
      const roots = me.teamId ? [...managed, me.teamId] : managed;
      const teamIds = idSet(roots.concat(roots.flatMap((r) => descendantTeamIds(teams, r))));
      return users.filter((u) => sameId(u.id, me.id) || sameId(u.managerId, me.id) || hasId(teamIds, u.teamId));
    }
    case 'rep':
      return [me];
  }
}

export interface VisibleRecordsInput {
  records: readonly RecordPayload[];
  users: readonly UserPayload[];
  teams: readonly TeamPayload[];
  territories: readonly TerritoryPayload[];
  settings: Pick<SettingsPayload, 'repsSeeOnlyOwnRecords'>;
  viewer: Viewer;
  /** Keep archived stores (the Stores list "Archived" toggle). iOS never shows them. */
  includeArchived?: boolean;
}

/**
 * `AppStore.visibleRecords`: never archived; admins see all; managers see unowned stores and stores owned by a
 * visible user; reps see everything when `repsSeeOnlyOwnRecords` is off, else their own, unowned ones, and those in a
 * territory that lists them (`assignedUserIds`, active or not, as iOS).
 */
export function visibleRecords(input: VisibleRecordsInput): RecordPayload[] {
  const { records, users, teams, territories, settings, viewer } = input;
  const active = input.includeArchived ? [...records] : records.filter((r) => !r.isArchived);
  const me = currentUser(users, viewer);
  if (!me) return active;
  if (viewer.role !== 'rep' || !settings.repsSeeOnlyOwnRecords) {
    if (viewer.role === 'manager') {
      const ids = idSet(visibleUsers(users, teams, viewer).map((u) => u.id));
      return active.filter((r) => !r.ownerId || hasId(ids, r.ownerId));
    }
    return active;
  }
  const myTerritories = idSet(territories.filter((t) => t.assignedUserIds.some((u) => sameId(u, me.id))).map((t) => t.id));
  return active.filter((r) => !r.ownerId || sameId(r.ownerId, me.id) || hasId(myTerritories, r.territoryId));
}

/**
 * `AppStore.canEdit(record:)`: managers, unowned or own stores, or anyone when reps may see every store. The console
 * also hides editing from reps entirely (permissions.ts `canEditRecords`), and the server has the last word.
 */
export function canEditRecord(
  record: Pick<RecordPayload, 'ownerId'>,
  viewer: Viewer,
  settings: Pick<SettingsPayload, 'repsSeeOnlyOwnRecords'>,
): boolean {
  return viewer.role !== 'rep' || !record.ownerId || sameId(record.ownerId, viewer.profileId) || !settings.repsSeeOnlyOwnRecords;
}

/**
 * `AppStore.leaderboardUsers(teamOnly:)`: every active user (leaderboards are company-public); "Team only" narrows to
 * the viewer's team and the teams below it. A viewer without a team gets everyone either way, as on iOS.
 */
export function leaderboardUsers(
  users: readonly UserPayload[],
  teams: readonly TeamPayload[],
  viewer: Viewer,
  teamOnly = false,
): UserPayload[] {
  const base = users.filter((u) => u.isActive);
  const teamId = currentUser(users, viewer)?.teamId;
  if (!teamOnly || !teamId) return base;
  const ids = idSet([...descendantTeamIds(teams, teamId), teamId]);
  return base.filter((u) => hasId(ids, u.teamId));
}

/**
 * `AppStore.manageableGoals`: admins every goal; managers goals of visible people, of their own team tree, and
 * company-wide ones; reps none.
 */
export function manageableGoals(
  goals: readonly GoalPayload[],
  users: readonly UserPayload[],
  teams: readonly TeamPayload[],
  viewer: Viewer,
): GoalPayload[] {
  if (viewer.role === 'rep') return [];
  if (viewer.role === 'admin') return [...goals];
  const userIds = idSet(visibleUsers(users, teams, viewer).map((u) => u.id));
  const myTeam = currentUser(users, viewer)?.teamId;
  const teamIds = idSet([...descendantTeamIds(teams, myTeam), myTeam]);
  return goals.filter((g) => {
    if (g.userId) return hasId(userIds, g.userId);
    if (g.teamId) return hasId(teamIds, g.teamId);
    return true;
  });
}

/** `AppStore.myGoals()`: the viewer's own goals, company-wide ones, and their team's. */
export function myGoals(goals: readonly GoalPayload[], users: readonly UserPayload[], viewer: Viewer): GoalPayload[] {
  const myTeam = currentUser(users, viewer)?.teamId;
  return goals.filter((g) => {
    // Swift compares optionals: a goal without userId matches a viewer without a profile id too.
    if (!g.userId) return !viewer.profileId || !g.teamId || sameId(g.teamId, myTeam);
    return sameId(g.userId, viewer.profileId);
  });
}
